import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { signOut } from '../slices/auth'
import type { RootState } from '../store'
import { useHydrateUser } from '../hooks/useAuth'
import {
  IconBell,
  IconChart,
  IconHistory,
  IconLogout,
  IconMap,
  IconSettings,
} from '../components/Icons'
import './AppShell.css'

const nav = [
  { to: '/app', end: true, key: 'Suivi', Icon: IconMap },
  { to: '/app/reports', end: false, key: 'Rapports', Icon: IconChart },
  { to: '/app/history', end: false, key: 'Historique', Icon: IconHistory },
  { to: '/app/settings', end: false, key: 'Paramètres', Icon: IconSettings },
] as const

export function AppShell() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const user = useSelector((s: RootState) => s.auth.user)
  useHydrateUser()

  const initials =
    `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.toUpperCase() ||
    user?.email?.[0]?.toUpperCase() ||
    '?'
  const unread = user?.unreadNotifications ?? 0

  return (
    <div className="shell">
      <aside className="shell__aside">
        <div className="shell__brand">
          <img src="/logo.png" alt="" width={42} height={42} className="shell__logo" />
          <div>
            <strong>
              Drive<span>Ops</span>
            </strong>
            <p>{t('Fleet tracking')}</p>
          </div>
        </div>
        <nav className="shell__nav" aria-label="Main">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `shell__link${isActive ? ' is-active' : ''}`}
            >
              <item.Icon size={18} className="shell__link-icon" />
              <span>{t(item.key)}</span>
            </NavLink>
          ))}
        </nav>
        <div className="shell__user">
          <div className="shell__avatar" aria-hidden="true">
            {initials}
          </div>
          <div className="shell__user-meta">
            <strong>
              {user?.firstName} {user?.lastName}
            </strong>
            <p>{user?.email}</p>
          </div>
          <button
            type="button"
            className="shell__logout"
            title={t('Déconnexion')}
            onClick={() => {
              dispatch(signOut())
              navigate('/login')
            }}
          >
            <IconLogout size={18} />
            <span>{t('Déconnexion')}</span>
          </button>
        </div>
      </aside>
      <div className="shell__workspace">
        <header className="shell__topbar">
          <div className="shell__topbar-brand">
            <img src="/logo.png" alt="" width={28} height={28} />
            <strong>
              Drive<span>Ops</span>
            </strong>
          </div>
          <button type="button" className="shell__bell" title={t('Notifications')} aria-label={t('Notifications')}>
            <IconBell size={20} />
            {unread > 0 && <em>{unread > 9 ? '9+' : unread}</em>}
          </button>
        </header>
        <main className="shell__main">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
